import { InstitutionRole, Role, WorkRole } from './enums';

/**
 * Matriz de permissões (fonte de verdade executável — documentada em docs/architecture.md §5).
 *
 * A autorização é sempre: usuário + instituição + trabalho + papel + ação + escopo.
 * Este módulo é puro: recebe o contexto já carregado do banco pela API.
 */
export const Action = {
  // Instituição
  INSTITUTION_READ: 'institution.read',
  INSTITUTION_MANAGE: 'institution.manage',
  INVITATION_CREATE: 'invitation.create',
  POLICY_MANAGE: 'policy.manage',
  POLICY_OVERRIDE_MANDATORY: 'policy.override_mandatory',
  WORK_CREATE: 'work.create',
  // Trabalho
  WORK_READ: 'work.read',
  WORK_UPDATE: 'work.update',
  WORK_PARTICIPANTS_MANAGE: 'work.participants.manage',
  WORK_ARCHIVE: 'work.archive',
  WORK_POLICY_EXCEPTION: 'work.policy_exception',
  WORK_POLICY_MIGRATE: 'work.policy_migrate',
  SCHEDULE_MANAGE: 'schedule.manage',
  EXTENSION_REQUEST: 'extension.request',
  EXTENSION_DECIDE: 'extension.decide',
  TASK_READ_DRAFTS: 'task.read_drafts',
  TASK_MANAGE: 'task.manage',
  TASK_DECLARE: 'task.declare',
  TASK_VALIDATE: 'task.validate',
  DELIVERABLE_MANAGE: 'deliverable.manage',
  DELIVERABLE_SUBMIT: 'deliverable.submit',
  DELIVERABLE_REVIEW: 'deliverable.review',
  FEEDBACK_CREATE: 'feedback.create',
  FEEDBACK_READ_PRIVATE: 'feedback.read_private',
  FILE_DOWNLOAD: 'file.download',
  CHECKIN_SUBMIT: 'checkin.submit',
  CHECKIN_REVIEW: 'checkin.review',
  MEETING_READ: 'meeting.read',
  MEETING_MANAGE: 'meeting.manage',
  REQUEST_CREATE: 'request.create',
  REQUEST_DECIDE: 'request.decide',
  AI_RUN: 'ai.run',
  AI_REVIEW: 'ai.review',
  REPORT_EXPORT: 'report.export',
  AUDIT_READ: 'audit.read',
} as const;
export type Action = (typeof Action)[keyof typeof Action];

const R = Role;
const ADMINS = [R.SUPER_ADMIN, R.ADMIN_INSTITUTION] as const;
const MANAGERS = [...ADMINS, R.COORDINATOR] as const;
const ADVISORS = [R.LEAD_ADVISOR, R.CO_ADVISOR] as const;
const STAFF = [...MANAGERS, ...ADVISORS] as const;

export const PERMISSIONS: Record<Action, readonly Role[]> = {
  'institution.read': [...MANAGERS, R.ADVISOR, R.STUDENT],
  'institution.manage': ADMINS,
  'invitation.create': MANAGERS,
  'policy.manage': MANAGERS,
  'policy.override_mandatory': ADMINS,
  'work.create': [...MANAGERS, R.ADVISOR],

  'work.read': [...STAFF, R.COLLABORATOR, R.STUDENT, R.REVIEWER],
  'work.update': [...MANAGERS, R.LEAD_ADVISOR],
  'work.participants.manage': [...MANAGERS, R.LEAD_ADVISOR],
  'work.archive': [...MANAGERS, R.LEAD_ADVISOR],
  'work.policy_exception': [...MANAGERS, R.LEAD_ADVISOR],
  'work.policy_migrate': [...MANAGERS, R.LEAD_ADVISOR],
  'schedule.manage': STAFF,
  'extension.request': STAFF,
  // A decisão final também depende da regra de política extension.approverRoles.
  'extension.decide': STAFF,
  'task.read_drafts': [...STAFF, R.COLLABORATOR],
  'task.manage': [...STAFF, R.COLLABORATOR],
  'task.declare': [R.STUDENT],
  'task.validate': STAFF,
  'deliverable.manage': [...STAFF, R.COLLABORATOR],
  'deliverable.submit': [R.STUDENT],
  'deliverable.review': [...STAFF, R.REVIEWER],
  'feedback.create': [...STAFF, R.COLLABORATOR, R.STUDENT, R.REVIEWER],
  'feedback.read_private': [...STAFF, R.COLLABORATOR, R.REVIEWER],
  'file.download': [...STAFF, R.COLLABORATOR, R.STUDENT, R.REVIEWER],
  'checkin.submit': [R.STUDENT],
  'checkin.review': STAFF,
  'meeting.read': [...STAFF, R.COLLABORATOR, R.STUDENT],
  'meeting.manage': STAFF,
  'request.create': [R.STUDENT],
  'request.decide': STAFF,
  'ai.run': STAFF,
  'ai.review': STAFF,
  'report.export': [...STAFF, R.STUDENT],
  'audit.read': [...MANAGERS, R.LEAD_ADVISOR],
};

export interface InstitutionRoleGrant {
  role: InstitutionRole;
  /** null = vale para a instituição inteira */
  programId: string | null;
}

export interface AccessContext {
  isSuperAdmin: boolean;
  /** Papéis institucionais ATIVOS do usuário na instituição do recurso. */
  institutionRoles: InstitutionRoleGrant[];
  /** Papéis ATIVOS do usuário no trabalho (vazio se não participa ou se o recurso não é um trabalho). */
  workRoles: WorkRole[];
  /** Programa do trabalho (para limitar coordenação por programa). */
  workProgramId?: string | null;
}

/** Papéis institucionais que concedem visão sobre trabalhos da instituição/programa. */
const SUPERVISORY: InstitutionRole[] = ['ADMIN_INSTITUTION', 'COORDINATOR'];

/**
 * Calcula os papéis efetivos para um recurso de trabalho.
 * - SUPER_ADMIN vem do usuário.
 * - ADMIN_INSTITUTION vale para toda a instituição.
 * - COORDINATOR vale para a instituição (programId null) ou somente para o programa do trabalho.
 * - ADVISOR/STUDENT institucionais NÃO dão acesso a trabalhos: só a participação no trabalho dá.
 */
export function effectiveWorkRoles(ctx: AccessContext): Set<Role> {
  const roles = new Set<Role>();
  if (ctx.isSuperAdmin) roles.add(Role.SUPER_ADMIN);
  for (const grant of ctx.institutionRoles) {
    if (!SUPERVISORY.includes(grant.role)) continue;
    if (grant.role === 'COORDINATOR' && grant.programId && grant.programId !== ctx.workProgramId) continue;
    roles.add(grant.role);
  }
  for (const wr of ctx.workRoles) roles.add(wr);
  return roles;
}

/** Papéis efetivos em escopo institucional (sem trabalho), opcionalmente restrito a um programa-alvo. */
export function effectiveInstitutionRoles(ctx: AccessContext, targetProgramId?: string | null): Set<Role> {
  const roles = new Set<Role>();
  if (ctx.isSuperAdmin) roles.add(Role.SUPER_ADMIN);
  for (const grant of ctx.institutionRoles) {
    if (grant.role === 'COORDINATOR' && grant.programId) {
      // Coordenação de programa só atua sobre o próprio programa.
      if (targetProgramId === undefined || targetProgramId !== grant.programId) continue;
    }
    roles.add(grant.role);
  }
  return roles;
}

export function hasPermission(roles: Iterable<Role>, action: Action): boolean {
  const allowed = PERMISSIONS[action];
  for (const r of roles) if (allowed.includes(r)) return true;
  return false;
}

/** O usuário enxerga o trabalho? (decide entre 404 e 403) */
export function canSeeWork(ctx: AccessContext): boolean {
  return hasPermission(effectiveWorkRoles(ctx), Action.WORK_READ);
}

export function canOnWork(ctx: AccessContext, action: Action): boolean {
  return hasPermission(effectiveWorkRoles(ctx), action);
}

export function canOnInstitution(ctx: AccessContext, action: Action, targetProgramId?: string | null): boolean {
  return hasPermission(effectiveInstitutionRoles(ctx, targetProgramId), action);
}

/** Lado docente/gestão (vê rascunhos, feedback privado etc.). */
export function isStaffSide(roles: Iterable<Role>): boolean {
  return hasPermission(roles, Action.TASK_READ_DRAFTS);
}

/** Lista de ações permitidas — enviada ao frontend para exibir/ocultar controles (a API continua validando). */
export function allowedActions(roles: Iterable<Role>): Action[] {
  const list = [...roles];
  return (Object.keys(PERMISSIONS) as Action[]).filter((a) => hasPermission(list, a));
}
